import type { Editor } from "@tiptap/react";
import type { ColorName, GroupName } from "./color-names";
import { useState } from "react";
import { colorGroups } from "./color-names";
import { Button } from "@acdc2/ui/components/button";
import { Command as CommandPrimitive } from "cmdk";
import { useTranslation } from "react-i18next";
import { Icon } from "@mdi/react";
import { mdiColorHelper, mdiFormatColorText } from "@mdi/js";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@acdc2/ui/components/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acdc2/ui/components/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@acdc2/ui/components/popover";

type Props = {
  editor: Editor;
};

export function TextColorPicker({ editor }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleSelect = (colorName: ColorName) => {
    editor.chain().focus().setColor(colorName).run();
    setOpen(false);
  };

  const currentColor: string | undefined =
    editor.getAttributes("textStyle").color;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                className="size-8 relative"
                variant="ghost"
                size="sm"
                role="combobox"
                aria-expanded={open}
              >
                <Icon
                  className="absolute"
                  path={mdiFormatColorText}
                  size="1.25rem"
                />
                <Icon
                  className="absolute"
                  path={mdiColorHelper}
                  size="1.25rem"
                  color={currentColor}
                />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{t("TextColorPicker.tooltip")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="p-0 mx-2">
        <Command>
          <CommandInput placeholder={t("TextColorPicker.input")} />
          <CommandList>
            <CommandEmpty>{t("TextColorPicker.empty")}</CommandEmpty>
            {Object.entries(colorGroups).map(([name, colors]) => {
              const translatedGroup = t(
                `TextColorPicker.groups.${name as GroupName}`,
              );

              return (
                <CommandGroup key={name} heading={translatedGroup}>
                  {colors.map((color) => {
                    const translatedColor = t(
                      `TextColorPicker.colors.${color}`,
                    );

                    return (
                      <CommandPrimitive.Item
                        key={color}
                        value={color}
                        keywords={[translatedGroup, translatedColor]}
                        onSelect={() => handleSelect(color)}
                        className="px-2 py-1.5 cursor-pointer hover:bg-accent flex flex-row gap-4"
                      >
                        <div
                          className="size-6 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span>{translatedColor}</span>
                      </CommandPrimitive.Item>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
